import { useState } from "react";
import { useNavigate } from "react-router-dom";
import tasksService from "../../services/tasks";
import "./AddTask.css";

export default function AddTask() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    jobTitle: "",
    pharmacy: "",
    from: "",
    to: "",
    priority: "Priority order",
    notes: "",
  });
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [createdJobId, setCreatedJobId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(''); // Clear error on input change
  };

  const handleGenerateQR = async () => {
    try {
      setLoading(true);
      setError('');

      // Validation
      if (!formData.from || !formData.to) {
        setError('Please fill in From and To fields');
        setLoading(false);
        return;
      }

      // Create task
      const task = await tasksService.createTask({
        from: formData.from,
        to: formData.to,
        priority: formData.priority,
        notes: formData.notes
      });

      setCreatedJobId(task.jobId);

      // Generate QR code
      const qrData = await tasksService.generateQRCode(task._id);
      setQrCodeUrl(qrData.qrImageUrl);
      setQrCode(qrData.qrCode);

      // Show modal
      setShowQRModal(true);
    } catch (err) {
      setError(err.message || 'Failed to create task');
      console.error('Create task error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCloseModal = () => {
    setShowQRModal(false);
    // Navigate to tasks page after closing modal
    navigate('/tasks');
  };

  return (
    <div className="add-task-page-container">
      {/* Header Section */}
      <section className="mq-dashboard-header">
        <div>
          <p className="mq-dashboard-welcome-label">Welcome Back,</p>
          <h1 className="mq-dashboard-welcome-name">Add new task</h1>
        </div>
      </section>

      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '4px',
          color: '#c33',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* Main Form Card */}
      <div className="add-task-card-wrapper">
        {/* Form Header */}
        <div className="add-task-form-header">
          <h2 className="add-task-form-title">Request A New Task</h2>
        </div>

        {/* Form Content */}
        <form className="add-task-form">
          {/* Job Title Field */}
          <div className="add-task-form-group">
            <label className="add-task-label">Job Title</label>
            <input
              type="text"
              name="jobTitle"
              className="add-task-input"
              placeholder="Enter here"
              value={formData.jobTitle}
              onChange={handleInputChange}
            />
          </div>

          {/* Pharmacy/Address Field */}
          <div className="add-task-form-group">
            <label className="add-task-label">Pharmacy/Address</label>
            <select
              name="pharmacy"
              className="add-task-select"
              value={formData.pharmacy}
              onChange={handleInputChange}
            >
              <option value="">Select</option>
              <option value="pharmacy1">Pharmacy 1</option>
              <option value="pharmacy2">Pharmacy 2</option>
              <option value="pharmacy3">Pharmacy 3</option>
            </select>
          </div>

          {/* From Field */}
          <div className="add-task-form-group">
            <label className="add-task-label">From</label>
            <input
              type="text"
              name="from"
              className="add-task-input"
              placeholder="Enter here"
              value={formData.from}
              onChange={handleInputChange}
            />
          </div>

          {/* To Field */}
          <div className="add-task-form-group">
            <label className="add-task-label">To</label>
            <input
              type="text"
              name="to"
              className="add-task-input"
              placeholder="Enter here"
              value={formData.to}
              onChange={handleInputChange}
            />
          </div>

          {/* Statistic Field */}
          <div className="add-task-form-group">
            <label className="add-task-label">Statistic</label>
            <select
              name="priority"
              className="add-task-select"
              value={formData.priority}
              onChange={handleInputChange}
            >
              <option value="Priority order">Priority order</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {/* Notes Field */}
          <div className="add-task-form-group add-task-form-group-notes">
            <label className="add-task-label">Notes</label>
            <textarea
              name="notes"
              className="add-task-textarea"
              placeholder="Enter notes here"
              value={formData.notes}
              onChange={handleInputChange}
              rows="6"
            />
          </div>

          {/* Generate QR Code Button */}
          <button
            type="button"
            className="add-task-btn-qr"
            onClick={handleGenerateQR}
            disabled={loading}
          >
            {loading ? 'Generating...' : 'Generate QR Code'}
          </button>
        </form>
      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="qr-modal-overlay">
          <div className="qr-modal-content">
            {/* Close Button */}
            <button
              className="qr-modal-close"
              onClick={handleCloseModal}
            >
              ✕
            </button>

            {/* Modal Header */}
            <h2 className="qr-modal-title">QR Code Successfully Generated</h2>
            <p className="qr-modal-subtitle">
              Job ID: {createdJobId} | Your QR code is ready to share.
            </p>

            {/* QR Code Image */}
            <div className="qr-modal-image-container">
              {qrCodeUrl ? (
                <img
                  src={qrCodeUrl}
                  alt="Generated QR Code"
                  className="qr-modal-image"
                />
              ) : (
                <p>Loading QR code...</p>
              )}
              {qrCode && (
                <p style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                  Code: {qrCode}
                </p>
              )}
            </div>

            {/* Print Button */}
            <button
              className="qr-modal-print-btn"
              onClick={handlePrint}
            >
              Print
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
